import { useState } from 'react';

// Personnages SVG français amusants
const PersonnageSVG = ({ id }: { id: string }) => {
  const personnages: Record<string, { couleurTenue: string; couleurVisage: string; accessoire: React.ReactElement }> = {
    'jean-jacques': {
      couleurTenue: '#DC143C',
      couleurVisage: '#FFD4A3',
      accessoire: (
        <g>
          {/* Casquette */}
          <ellipse cx="50" cy="25" rx="22" ry="8" fill="#DC143C" />
          <rect x="28" y="22" width="44" height="6" rx="3" fill="#B01030" />
        </g>
      )
    },
    'maurice': {
      couleurTenue: '#4B5563',
      couleurVisage: '#F4C2A0',
      accessoire: (
        <g>
          {/* Lunettes */}
          <circle cx="42" cy="48" r="8" fill="none" stroke="#333" strokeWidth="2" />
          <circle cx="58" cy="48" r="8" fill="none" stroke="#333" strokeWidth="2" />
          <line x1="50" y1="48" x2="50" y2="48" stroke="#333" strokeWidth="2" />
        </g>
      )
    },
    'philippe': {
      couleurTenue: '#1F2937',
      couleurVisage: '#FFDAB3',
      accessoire: (
        <g>
          {/* Cravate */}
          <polygon points="50,65 45,70 50,85 55,70" fill="#DC143C" />
          <rect x="47" y="62" width="6" height="5" fill="#1F2937" />
        </g>
      )
    },
    'bernard': {
      couleurTenue: '#F59E0B',
      couleurVisage: '#FFE4C4',
      accessoire: (
        <g>
          {/* Sourire large */}
          <path d="M 38 55 Q 50 62 62 55" fill="none" stroke="#333" strokeWidth="2" strokeLinecap="round" />
          <circle cx="42" cy="48" r="2" fill="#333" />
          <circle cx="58" cy="48" r="2" fill="#333" />
        </g>
      )
    },
    'gerard': {
      couleurTenue: '#10B981',
      couleurVisage: '#F5D5B8',
      accessoire: (
        <g>
          {/* Bandeau sport */}
          <rect x="30" y="28" width="40" height="6" rx="3" fill="#10B981" />
          <circle cx="42" cy="48" r="2" fill="#333" />
          <circle cx="58" cy="48" r="2" fill="#333" />
        </g>
      )
    },
    'jacqueline': {
      couleurTenue: '#EC4899',
      couleurVisage: '#FFE0D0',
      accessoire: (
        <g>
          {/* Foulard */}
          <ellipse cx="50" cy="65" rx="18" ry="6" fill="#EC4899" opacity="0.8" />
          <circle cx="42" cy="48" r="2" fill="#333" />
          <circle cx="58" cy="48" r="2" fill="#333" />
        </g>
      )
    },
    'monique': {
      couleurTenue: '#8B5CF6',
      couleurVisage: '#FFD9C0',
      accessoire: (
        <g>
          {/* Collier */}
          <circle cx="45" cy="68" r="3" fill="#FFD700" />
          <circle cx="50" cy="70" r="3" fill="#FFD700" />
          <circle cx="55" cy="68" r="3" fill="#FFD700" />
          <circle cx="42" cy="48" r="2" fill="#333" />
          <circle cx="58" cy="48" r="2" fill="#333" />
        </g>
      )
    },
    'rene': {
      couleurTenue: '#6B7280',
      couleurVisage: '#F0D0A8',
      accessoire: (
        <g>
          {/* Moustache */}
          <path d="M 40 56 Q 45 58 50 56 Q 55 58 60 56" fill="#333" />
          <circle cx="42" cy="48" r="2" fill="#333" />
          <circle cx="58" cy="48" r="2" fill="#333" />
        </g>
      )
    }
  };

  const perso = personnages[id] || personnages['jean-jacques'];

  return (
    <svg viewBox="0 0 100 100" className="w-full h-full">
      {/* Corps / Tenue */}
      <rect x="30" y="65" width="40" height="35" rx="8" fill={perso.couleurTenue} />
      
      {/* Cou */}
      <rect x="42" y="58" width="16" height="10" fill={perso.couleurVisage} />
      
      {/* Visage */}
      <circle cx="50" cy="45" r="18" fill={perso.couleurVisage} />
      
      {/* Accessoire spécifique */}
      {perso.accessoire}
      
      {/* Yeux par défaut (si pas dans accessoire) */}
      {!['maurice', 'bernard', 'gerard', 'jacqueline', 'monique', 'rene'].includes(id) && (
        <>
          <circle cx="42" cy="48" r="2" fill="#333" />
          <circle cx="58" cy="48" r="2" fill="#333" />
        </>
      )}
      
      {/* Bouche par défaut (si pas dans accessoire) */}
      {!['bernard', 'rene'].includes(id) && (
        <line x1="45" y1="56" x2="55" y2="56" stroke="#333" strokeWidth="2" strokeLinecap="round" />
      )}
    </svg>
  );
};

// Définition des avatars personnages français
export const AVATARS = [
  {
    id: 'jean-jacques',
    name: 'Jean-Jacques le Costaud',
    label: 'Jean-Jacques le Costaud',
    description: 'Toujours prêt à relever les défis'
  },
  {
    id: 'maurice',
    name: 'Maurice le Discret',
    label: 'Maurice le Discret',
    description: 'Efficace et méthodique'
  },
  {
    id: 'philippe',
    name: 'Philippe le Sérieux',
    label: 'Philippe le Sérieux',
    description: 'Professionnel jusqu\'au bout'
  },
  {
    id: 'bernard',
    name: 'Bernard le Souriant',
    label: 'Bernard le Souriant',
    description: 'Toujours de bonne humeur'
  },
  {
    id: 'gerard',
    name: 'Gérard le Dynamique',
    label: 'Gérard le Dynamique',
    description: 'Plein d\'énergie et d\'idées'
  },
  {
    id: 'jacqueline',
    name: 'Jacqueline la Pro',
    label: 'Jacqueline la Pro',
    description: 'Élégante et compétente'
  },
  {
    id: 'monique',
    name: 'Monique la Fiable',
    label: 'Monique la Fiable',
    description: 'On peut toujours compter sur elle'
  },
  {
    id: 'rene',
    name: 'René le Classique',
    label: 'René le Classique',
    description: 'L\'expérience avant tout'
  }
];

interface AvatarPickerProps {
  selectedAvatarId?: string;
  onSelect: (avatarId: string) => void;
}

export default function AvatarPicker({ selectedAvatarId, onSelect }: AvatarPickerProps) {
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-white">
          Choisissez votre personnage
        </h3>
        <p className="text-sm text-gray-400">
          Sélectionnez votre avatar
        </p>
      </div>

      <div className="grid grid-cols-4 gap-4">
        {AVATARS.map((avatar) => {
          const isSelected = selectedAvatarId === avatar.id;
          const isHovered = hoveredId === avatar.id;

          return (
            <button
              key={avatar.id}
              type="button"
              onClick={() => onSelect(avatar.id)}
              onMouseEnter={() => setHoveredId(avatar.id)}
              onMouseLeave={() => setHoveredId(null)}
              className={`
                relative flex flex-col items-center justify-center p-4 rounded-lg
                border-2 transition-all duration-200 cursor-pointer
                ${
                  isSelected
                    ? 'border-[#DC143C] bg-[#DC143C]/10'
                    : 'border-gray-800 bg-[#111111] hover:border-gray-700'
                }
                ${isHovered && !isSelected ? 'transform scale-105' : ''}
              `}
            >
              {/* Badge sélectionné */}
              {isSelected && (
                <div className="absolute -top-2 -right-2 w-6 h-6 bg-[#DC143C] rounded-full flex items-center justify-center">
                  <i className="ri-check-line text-white text-sm"></i>
                </div>
              )}

              {/* Avatar SVG */}
              <div
                className={`
                  w-16 h-16 rounded-full flex items-center justify-center mb-2 overflow-hidden
                  bg-[#1a1a1a]
                  ${isSelected ? 'ring-2 ring-[#DC143C] ring-offset-2 ring-offset-[#0a0a0a]' : ''}
                `}
              >
                <PersonnageSVG id={avatar.id} />
              </div>

              {/* Label */}
              <span
                className={`
                  text-xs font-medium text-center whitespace-nowrap
                  ${isSelected ? 'text-[#DC143C]' : 'text-gray-300'}
                `}
              >
                {avatar.label.split(' ')[0]}
              </span>
            </button>
          );
        })}
      </div>

      {/* Aperçu du personnage sélectionné */}
      {selectedAvatarId && (
        <div className="flex items-center gap-4 p-4 bg-[#111111] border border-gray-800 rounded-lg">
          <div className="w-20 h-20 rounded-full flex items-center justify-center bg-[#1a1a1a] overflow-hidden flex-shrink-0">
            <PersonnageSVG id={selectedAvatarId} />
          </div>
          <div className="flex-1">
            <h4 className="text-white font-semibold mb-1">
              {AVATARS.find(a => a.id === selectedAvatarId)?.label}
            </h4>
            <p className="text-sm text-gray-400">
              {AVATARS.find(a => a.id === selectedAvatarId)?.description}
            </p>
          </div>
        </div>
      )}

      {/* Message d'aide */}
      <div className="flex items-start gap-2 p-3 bg-[#111111] border border-gray-800 rounded-lg">
        <i className="ri-information-line text-[#DC143C] text-lg mt-0.5"></i>
        <p className="text-sm text-gray-400">
          Votre avatar sera visible dans votre profil et dans la barre de navigation.
          Vous pouvez le changer à tout moment.
        </p>
      </div>
    </div>
  );
}

// Fonction utilitaire pour obtenir un avatar par ID
export function getAvatarById(avatarId?: string) {
  if (!avatarId) return null;
  return AVATARS.find((a) => a.id === avatarId) || null;
}

// Composant d'affichage d'avatar (pour TopRightUser et ComptePage)
interface AvatarDisplayProps {
  avatarId?: string | null;
  userName?: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export function AvatarDisplay({ avatarId, userName, size = 'md', className = '' }: AvatarDisplayProps) {
  const avatar = getAvatarById(avatarId ?? undefined);

  // Tailles conteneur
  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-10 h-10',
    lg: 'w-16 h-16'
  };

  // Tailles texte fallback initiale
  const textSizeClasses = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-xl'
  };

  // Si un avatar est sélectionné, l'afficher
  if (avatar) {
    return (
      <div
        className={`
          ${sizeClasses[size]} rounded-full flex items-center justify-center
          bg-[#1a1a1a] overflow-hidden flex-shrink-0
          ${className}
        `}
        title={avatar.name}
      >
        <PersonnageSVG id={avatar.id} />
      </div>
    );
  }

  // Sinon, afficher l'initiale par défaut
  const initial = userName?.trim().charAt(0).toUpperCase() || 'U';
  return (
    <div
      className={`
        ${sizeClasses[size]} rounded-full flex items-center justify-center flex-shrink-0
        bg-gradient-to-br from-[#DC143C]/80 to-[#8B0000] text-white font-semibold
        ${textSizeClasses[size]}
        ${className}
      `}
      title={userName || 'Utilisateur'}
    >
      {initial}
    </div>
  );
}